import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { LocationSelect } from '../components/Operation';
import { SalePanel } from '../ops/SalePanel';
import { SalesHistory } from '../ops/SalesHistory';
import { useLoad } from '../lib/useAsync';
import { bogotaDay, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

/** Venta del local: precio negociable, pagos mixtos e historial con devoluciones. */
export function Pos() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user } = useAuth();
  const stores = (user?.locations ?? []).filter((l) => l.type === 'STORE');
  const [selected, setSelected] = useState<number | null>(null);
  const locationId = selected ?? stores[0]?.id ?? null;
  const name = stores.find((l) => l.id === locationId)?.name ?? '';

  const methods = useLoad(() => api.paymentMethods(), []);
  const stock = useLoad(() => (locationId ? api.inventorySummary(locationId) : Promise.resolve(null)), [locationId]);
  const sales = useLoad(() => (locationId ? api.sales({ location_id: locationId, channel: 'store', limit: 30 }) : Promise.resolve([])), [locationId]);
  const refresh = () => {
    void stock.reload();
    void sales.reload();
  };

  const today = bogotaDay(new Date());
  const todays = (sales.data ?? []).filter((s) => s.status === 'ACTIVE' && bogotaDay(s.sold_at) === today);
  const soldLines = todays.flatMap((s) => s.lines);
  const negotiated = soldLines.length ? Math.round((soldLines.filter((l) => l.negotiated).length / soldLines.length) * 100) : 0;

  return (
    <>
      <LocationSelect id="posLocation" locations={stores} value={locationId} onChange={setSelected} />
      <div className="kpis">
        <div className="kpi">
          <div className="lab">{t('pos.kpiStock')}</div>
          <div className="val" id="pStock">{formatNumber(stock.data?.units ?? 0, lang)}</div>
          <div className="sub">{t('pos.kpiStockSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('pos.kpiSales')}</div>
          <div className="val" id="pSales">{formatMoney(todays.reduce((s, r) => s + r.total, 0), lang)}</div>
          <div className="sub" id="pUnits">{t('ops.unitsCount', { n: formatNumber(todays.reduce((s, r) => s + r.units - r.returned_units, 0), lang) })}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('pos.kpiNegotiated')}</div>
          <div className="val" id="pNegotiated">{t('pos.percent', { n: negotiated })}</div>
          <div className="sub">{t('pos.kpiNegotiatedSub')}</div>
        </div>
      </div>
      <SalePanel
        idPrefix="pos" channel="store" locationId={locationId} methods={methods.data ?? []} onSold={refresh}
        header={<><h2 id="posName">{name}</h2><p>{t('pos.help')}</p></>}
      />
      <div style={{ marginTop: 14 }}>
        <SalesHistory sales={sales.data ?? []} onChanged={refresh} methods={methods.data ?? []} />
      </div>
    </>
  );
}
